import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Select } from '@/components/Select';
import { Modal } from '@/components/Modal';
import { getTrackerClicks } from '@/api/tracker-stats';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getFlows } from '@/api/tracker-flows';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { GoogleAdsCell, GoogleAdsDetails, GOOGLE_ADS_FILTER_OPTIONS } from '@/components/tracker/GoogleAdsAttribution';
import { AsaCell, AsaDetails, ASA_FILTER_OPTIONS } from '@/components/tracker/AsaAttribution';
import type { TrackerClickRow } from '@/api/tracker-types';
import { formatDateTime } from '@/lib/datetime';

function fmtCents(c: number) {
  return c ? `$${(c / 100).toFixed(2)}` : '—';
}

function shortId(id: string) {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export function TrackerClicks() {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(weekAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [campaignId, setCampaignId] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [flowId, setFlowId] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [status, setStatus] = useState<'' | 'any' | 'reg' | 'dep' | 'none'>('');
  const [search, setSearch] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [sourceBundleId, setSourceBundleId] = useState('');
  const [freelancer, setFreelancer] = useState<'' | '1' | '0'>('');
  const [googleAds, setGoogleAds] = useState<'' | '1' | '0'>('');
  const [asa, setAsa] = useState<'' | '1' | '0'>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<TrackerClickRow | null>(null);
  const limit = 50;

  const { data: campaigns = [] } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const { data: flows = [] } = useQuery({
    queryKey: ['tracker-flows', campaignId],
    queryFn: () => getFlows(campaignId),
    enabled: !!campaignId,
  });

  const campaignName = (id: string | null) => campaigns.find((c) => c.id === id)?.name || (id ? shortId(id) : '—');
  const flowName = (id: string | null) => flows.find((f) => f.id === id)?.name || (id ? shortId(id) : '—');

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['tracker-clicks', from, to, campaignId, labelFilter, flowId, countries, status, search, sourceType, sourceBundleId, freelancer, googleAds, asa, page],
    queryFn: () => getTrackerClicks({
      from: from ? `${from} 00:00:00` : undefined,
      to: to ? `${to} 23:59:59` : undefined,
      campaign_id: campaignId || undefined,
      label: labelFilter || undefined,
      flow_id: flowId || undefined,
      countries: countries.length ? countries : undefined,
      status: status || undefined,
      search: search || undefined,
      source_type: sourceType || undefined,
      source_bundle_id: sourceBundleId.trim() || undefined,
      freelancer: freelancer || undefined,
      google_ads: googleAds || undefined,
      asa: asa || undefined,
      page,
      limit,
    }),
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const labelValues = useMemo(
    () => Array.from(new Set(campaigns.map((c) => c.label).filter((l): l is string => !!l))).sort(),
    [campaigns]
  );
  const labelOptions = useMemo(() => [
    { value: '', label: 'All labels' },
    ...labelValues.map((l) => ({ value: l, label: l })),
  ], [labelValues]);
  const campaignOptions = useMemo(() => [
    { value: '', label: 'All campaigns' },
    ...campaigns
      .filter((c) => !labelFilter || c.label === labelFilter)
      .map((c) => ({ value: c.id, label: c.name })),
  ], [campaigns, labelFilter]);

  const flowOptions = useMemo(() => [
    { value: '', label: 'All flows' },
    ...flows.map((f) => ({ value: f.id, label: f.name })),
  ], [flows]);

  const statusOptions = [
    { value: '', label: 'All clicks' },
    { value: 'any', label: 'With any conversion' },
    { value: 'reg', label: 'With registration' },
    { value: 'dep', label: 'With deposit' },
    { value: 'none', label: 'No conversion' },
  ];

  const freelancerOptions = [
    { value: '', label: 'All traffic' },
    { value: '1', label: 'Freelance only' },
    { value: '0', label: 'Non-freelance' },
  ];

  const sourceTypeOptions = [
    { value: '', label: 'All sources' },
    { value: 'organic', label: 'organic' },
    { value: 'organic_force', label: 'organic_force' },
    { value: 'referral_force', label: 'referral_force' },
    { value: 'referral_click', label: 'referral_click' },
    { value: 'ad_click', label: 'ad_click' },
    { value: 'playable', label: 'playable' },
  ];

  const sourceBadge = (t: string | null) => {
    if (!t) return <span className="text-xs text-content-subtle">—</span>;
    const cls =
      t === 'ad_click'        ? 'bg-info-soft text-info' :
      t === 'referral_click'  ? 'bg-primary/10 text-primary' :
      t === 'referral_force'  ? 'bg-fuchsia-100 text-fuchsia-800' :
      t === 'organic_force'   ? 'bg-warning-soft text-warning' :
      t === 'playable'        ? 'bg-success-soft text-success' :
                                'bg-surface-muted text-content-muted';
    return <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${cls}`}>{t}</span>;
  };

  const columns = [
    {
      key: 'created_at', label: 'Time',
      render: (r: TrackerClickRow) => (
        <span className="text-xs text-content-muted whitespace-nowrap">
          {formatDateTime(r.created_at)}
        </span>
      ),
    },
    {
      key: 'id', label: 'Click ID',
      render: (r: TrackerClickRow) => (
        <span className="font-mono text-xs text-content-muted" title={r.id}>{shortId(r.id)}</span>
      ),
    },
    {
      key: 'campaign_id', label: 'Campaign',
      render: (r: TrackerClickRow) => <span className="text-xs">{campaignName(r.campaign_id)}</span>,
    },
    {
      key: 'flow_id', label: 'Flow',
      render: (r: TrackerClickRow) => <span className="text-xs">{flowName(r.flow_id)}</span>,
    },
    {
      key: 'country', label: 'Country',
      render: (r: TrackerClickRow) => <span className="text-xs">{r.country || '—'}</span>,
    },
    {
      key: 'device', label: 'Device',
      render: (r: TrackerClickRow) => (
        <span className="text-xs text-content-muted">
          {[r.device_type, r.os].filter(Boolean).join(' / ') || '—'}
        </span>
      ),
    },
    {
      key: 'is_freelancer', label: 'Freelance',
      render: (r: TrackerClickRow) => (
        r.is_freelancer
          ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400">freelance</span>
          : <span className="text-xs text-content-subtle">—</span>
      ),
    },
    {
      key: 'is_unique', label: 'Unique',
      render: (r: TrackerClickRow) => (
        <span className={`text-xs ${r.is_unique ? 'text-success' : 'text-content-subtle'}`}>
          {r.is_unique ? 'yes' : 'no'}
        </span>
      ),
    },
    {
      key: 'conv', label: 'Conversion',
      render: (r: TrackerClickRow) => {
        const parts: string[] = [];
        if (r.has_reg) parts.push('reg');
        if (r.has_dep) parts.push('dep');
        if (parts.length === 0) return <span className="text-xs text-content-subtle">—</span>;
        return (
          <span className="inline-flex gap-1">
            {parts.map((p) => (
              <span key={p} className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${p === 'dep' ? 'bg-success-soft text-success' : 'bg-info-soft text-info'}`}>{p}</span>
            ))}
          </span>
        );
      },
    },
    {
      key: 'payout_cents', label: 'Payout',
      render: (r: TrackerClickRow) => <span className="text-xs">{fmtCents(r.payout_cents)}</span>,
    },
    {
      key: 'source_type', label: 'Source',
      render: (r: TrackerClickRow) => (
        <div className="flex flex-col gap-0.5">
          {sourceBadge(r.source_type)}
          {r.source_bundle_id && (
            <span className="text-[11px] text-content-subtle font-mono truncate max-w-[180px]" title={r.source_bundle_id}>
              {r.source_bundle_id}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'google_ads', label: 'Google Ads',
      render: (r: TrackerClickRow) => <GoogleAdsCell sub3={r.sub_id_3} sub4={r.sub_id_4} />,
    },
    {
      key: 'asa', label: 'ASA',
      render: (r: TrackerClickRow) => <AsaCell isAsa={r.is_asa} campaignId={r.asa_campaign_id} adGroupId={r.asa_adgroup_id} keywordId={r.asa_keyword_id} />,
    },
    {
      key: 'sub_id_1', label: 'sub_id_1',
      render: (r: TrackerClickRow) => (
        <span className="text-xs text-content-muted font-mono">{r.sub_id_1 || '—'}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Tracker Clicks</h1>
          <p className="text-sm text-content-subtle mt-1">Per-click log with conversion status</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-sm px-3 py-1.5 border border-border-strong rounded-md hover:bg-surface-muted disabled:opacity-50"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="bg-surface border border-border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div>
          <label className="block text-xs text-content-subtle mb-1">From</label>
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">To</label>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Label</label>
          <Select
            value={labelFilter}
            options={labelOptions}
            onChange={(v) => {
              setLabelFilter(v);
              setPage(1);
              if (v && campaignId) {
                const c = campaigns.find((x) => x.id === campaignId);
                if (!c || c.label !== v) { setCampaignId(''); setFlowId(''); }
              }
            }}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Campaign</label>
          <Select value={campaignId} options={campaignOptions} onChange={(v) => { setCampaignId(v); setFlowId(''); setPage(1); }} className="w-full" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Flow</label>
          <Select value={flowId} options={flowOptions} onChange={(v) => { setFlowId(v); setPage(1); }} className="w-full" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-content-subtle mb-1">Countries</label>
          <CountryMultiSelect value={countries} onChange={(v) => { setCountries(v); setPage(1); }} placeholder="All countries" size="sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Status</label>
          <Select value={status} options={statusOptions} onChange={(v) => { setStatus(v as typeof status); setPage(1); }} className="w-full" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Source type</label>
          <Select value={sourceType} options={sourceTypeOptions} onChange={(v) => { setSourceType(v); setPage(1); }} className="w-full" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Freelance</label>
          <Select
            value={freelancer}
            options={freelancerOptions}
            onChange={(v) => { setFreelancer(v as '' | '1' | '0'); setPage(1); }}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Google Ads</label>
          <Select
            value={googleAds}
            options={GOOGLE_ADS_FILTER_OPTIONS}
            onChange={(v) => { setGoogleAds(v as '' | '1' | '0'); setPage(1); }}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Apple Search Ads</label>
          <Select
            value={asa}
            options={ASA_FILTER_OPTIONS}
            onChange={(v) => { setAsa(v as '' | '1' | '0'); setPage(1); }}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Source bundle</label>
          <input
            type="text" value={sourceBundleId}
            onChange={(e) => { setSourceBundleId(e.target.value); setPage(1); }}
            placeholder="com.example.app"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
        </div>
        <div className="md:col-span-4 lg:col-span-6">
          <label className="block text-xs text-content-subtle mb-1">Search (click_id, sub_id_1 or gclid prefix)</label>
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="paste click_id…" className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono" />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={isLoading}
        keyExtractor={(r) => r.id}
        onRowClick={(r) => setSelected(r)}
      />

      <div className="mt-4 flex items-center justify-between text-sm text-content-muted">
        <div>
          {total.toLocaleString()} click{total === 1 ? '' : 's'} • page {page} / {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 border border-border-strong rounded-md disabled:opacity-50 hover:bg-surface-muted"
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 border border-border-strong rounded-md disabled:opacity-50 hover:bg-surface-muted"
          >
            Next
          </button>
        </div>
      </div>

      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Click details" size="lg">
        {selected && (
          <div className="space-y-3 text-sm">
            <Field label="Click ID" mono>{selected.id}</Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Time">{formatDateTime(selected.created_at)}</Field>
              <Field label="Unique">{selected.is_unique ? 'yes' : 'no'}</Field>
              <Field label="Campaign">{campaignName(selected.campaign_id)}</Field>
              <Field label="Flow">{flowName(selected.flow_id)}</Field>
              <Field label="Offer ID" mono>{selected.offer_id || '—'}</Field>
              <Field label="Domain ID" mono>{selected.domain_id}</Field>
              <Field label="Country">{selected.country || '—'}</Field>
              <Field label="Region / City">{[selected.region, selected.city].filter(Boolean).join(', ') || '—'}</Field>
              <Field label="Device">{[selected.device_type, selected.os, selected.browser].filter(Boolean).join(' / ') || '—'}</Field>
              <Field label="Language">{selected.language || '—'}</Field>
              <Field label="IP" mono>{selected.ip || '—'}</Field>
              <Field label="Payout">{fmtCents(selected.payout_cents)}</Field>
              <Field label="Freelance">{selected.is_freelancer ? 'yes' : 'no'}</Field>
            </div>
            <Field label="User-Agent">{selected.user_agent || '—'}</Field>
            <Field label="Referrer" mono>{selected.referrer || '—'}</Field>
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Field key={i} label={`sub_id_${i}`} mono>
                  {(selected as any)[`sub_id_${i}`] || '—'}
                </Field>
              ))}
            </div>
            <GoogleAdsDetails sub3={selected.sub_id_3} sub4={selected.sub_id_4} appInstanceId={selected.app_instance_id} />
            <AsaDetails isAsa={selected.is_asa} campaignId={selected.asa_campaign_id} adGroupId={selected.asa_adgroup_id} keywordId={selected.asa_keyword_id} />
            <div className="border-t pt-3 mt-3">
              <div className="text-[11px] uppercase tracking-wide text-content-subtle mb-2">Source attribution</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Source type">{sourceBadge(selected.source_type)}</Field>
                <Field label="Source bundle" mono>{selected.source_bundle_id || '—'}</Field>
                <Field label="Source app" mono>{selected.source_app_id || '—'}</Field>
                <Field label="Source ad" mono>{selected.source_ad_id || '—'}</Field>
                <Field label="Source campaign" mono>{selected.source_campaign_id || '—'}</Field>
              </div>
            </div>
            <div className="flex gap-2">
              {selected.has_reg ? <span className="px-2 py-1 rounded bg-info-soft text-info text-xs font-medium">reg</span> : null}
              {selected.has_dep ? <span className="px-2 py-1 rounded bg-success-soft text-success text-xs font-medium">dep</span> : null}
              {!selected.has_reg && !selected.has_dep ? <span className="text-xs text-content-subtle">No conversions yet</span> : null}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</div>
      <div className={`text-sm text-content break-all ${mono ? 'font-mono' : ''}`}>{children}</div>
    </div>
  );
}
